<template>
  <div class="space-y-8">
    <section v-if="!hasBusiness && !serviceListLoading" class="rounded-[32px] border border-orange-100 bg-[linear-gradient(180deg,#fffdfa_0%,#fff7f0_100%)] px-6 py-12 shadow-sm lg:px-10 lg:py-16">
      <div class="mx-auto flex max-w-3xl flex-col items-center text-center">
        <div class="flex h-20 w-20 items-center justify-center rounded-full bg-[linear-gradient(135deg,#fff1e3_0%,#ffffff_100%)] text-4xl shadow-sm ring-1 ring-orange-100">
          <i class="ri-building-4-line text-brand"></i>
        </div>
        <h2 class="mt-6 text-4xl font-semibold tracking-tight text-ink">Welcome to VendorHub</h2>
        <p class="mt-4 max-w-2xl text-lg leading-8 text-muted">
          Register your business entity, then add venue properties or service vendor brands under it.
        </p>

        <div class="mt-10 grid w-full max-w-2xl gap-4 rounded-[28px] border border-orange-100 bg-white p-6 shadow-sm md:grid-cols-3">
          <div class="flex flex-col items-center text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff4e8] text-2xl text-brand">
              <i class="ri-building-line"></i>
            </div>
            <p class="mt-3 text-lg font-semibold text-ink">Business</p>
            <p class="mt-1 text-sm text-muted">Legal entity, GST, bank</p>
          </div>
          <div class="flex flex-col items-center text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff4e8] text-2xl text-brand">
              <i class="ri-bank-line"></i>
            </div>
            <p class="mt-3 text-lg font-semibold text-ink">Venue</p>
            <p class="mt-1 text-sm text-muted">Property + spaces + menus</p>
          </div>
          <div class="flex flex-col items-center text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff4e8] text-2xl text-brand">
              <i class="ri-camera-line"></i>
            </div>
            <p class="mt-3 text-lg font-semibold text-ink">Service vendor</p>
            <p class="mt-1 text-sm text-muted">Photography, catering, decor...</p>
          </div>
        </div>

        <button
          type="button"
          class="mt-10 rounded-2xl bg-brand px-8 py-4 text-base font-semibold text-white shadow-sm transition hover:bg-brandDark"
          @click="navigateToBusinessEditor()"
        >
          Register your first business
        </button>
      </div>
    </section>

    <section v-else class="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div class="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 class="text-lg font-semibold text-ink">Businesses</h3>
          <p class="text-sm text-muted">Manage your business records here.</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <button type="button" class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" :disabled="serviceListLoading" @click="fetchBusinessVendors()">
            {{ serviceListLoading ? 'Refreshing...' : 'Refresh' }}
          </button>
          <button type="button" class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="navigateToBusinessEditor()">
            {{ hasBusiness ? 'Add another business' : 'Add business' }}
          </button>
        </div>
      </div>

      <div v-if="serviceListError" class="mb-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
        {{ serviceListError }}
      </div>

      <div v-if="serviceListLoading" class="rounded-3xl border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-muted">
        Loading business vendors...
      </div>

      <div v-else-if="serviceVendors.length" class="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        <article
          v-for="business in serviceVendors"
          :key="business.id"
          class="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm transition hover:border-brand/40 hover:shadow-md"
        >
          <button type="button" class="block w-full text-left" @click="navigateToBusinessDetail(business)">
            <div class="relative mb-5 overflow-hidden rounded-[24px] border border-orange-100 bg-[linear-gradient(135deg,#fff7f0_0%,#fff1e4_48%,#fffaf6_100%)]">
              <div class="pointer-events-none absolute inset-0">
                <div class="absolute -left-10 top-4 h-32 w-32 rounded-full bg-brand/20 blur-3xl"></div>
                <div class="absolute -right-6 bottom-2 h-28 w-28 rounded-full bg-[#f6a96c]/30 blur-3xl"></div>
                <div class="absolute inset-x-10 top-8 h-20 rounded-full bg-white/40 blur-2xl"></div>
              </div>
              <img
                :src="getBusinessImageSrc(business.image)"
                :alt="business.businessName"
                class="relative z-10 h-52 w-full object-contain p-4"
              >
            </div>

            <div class="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p class="text-2xl font-semibold text-ink">{{ business.businessName }}</p>
                <p class="mt-2 text-sm text-muted">{{ business.badge }}<span v-if="business.address"> • {{ business.address }}</span></p>
              </div> 
            </div>
          </button>

          <div class="mt-5 flex items-center justify-between gap-3">
            <div class="flex flex-wrap gap-3">
              <button
                type="button"
                class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
                @click="navigateToBusinessDetail(business)"
              >
                View details
              </button> 
            </div>
            <div class="relative">
              <button
                type="button"
                class="flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-lg text-ink transition hover:border-brand hover:text-brand"
                @click="toggleBusinessMenu(business.id)"
              >
                <i class="ri-more-2-fill"></i>
              </button>
              <div
                v-if="openBusinessMenuId === business.id"
                class="absolute right-0 top-14 z-20 min-w-[190px] rounded-2xl border border-gray-100 bg-white p-2 shadow-xl"
              > 
                <button type="button" class="flex w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-ink transition hover:bg-[#fff3e5] hover:text-brand" @click="handleVendorMenuAction('edit', business)">
                  Edit business
                </button>
                <button type="button" class="flex w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-red-600 transition hover:bg-red-50" @click="handleVendorMenuAction('delete', business)">
                  Delete business
                </button>
              </div>
            </div>
          </div>
        </article>
      </div>

    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import api from '@/services/vendor/api'
import { getAuthUserId } from '@/services/auth'

const router = useRouter()

const serviceVendors = ref([])
const vendorId = ref(getAuthUserId())
const serviceListLoading = ref(false)
const serviceListError = ref('')
const openBusinessMenuId = ref(null)
const BUSINESS_IMAGE_PLACEHOLDER = 'https://dummyimage.com/960x640/fff1e6/8b5e3c&text=Business'

const hasBusiness = computed(() => serviceVendors.value.length > 0)

const hasBusinessImage = (value) => {
  if (value == null) return false
  const normalized = String(value).trim()
  return normalized !== '' && normalized !== '0'
}

const getBusinessImageSrc = (value) => (hasBusinessImage(value) ? value : BUSINESS_IMAGE_PLACEHOLDER)

const normalizeImage = (item) => item?.img || item?.image || item?.business_img || item?.photo || ''

const normalizeBusinessVendor = (item, index) => ({
  id: item?.id || item?.business_vendor_id || item?.vendor_id || item?.business_id || `vendor-${index}`,
  sId: item?.s_id || item?.seller_id || item?.vendor_owner_id || '',
  businessName: item?.business_name || item?.name || item?.title || 'Untitled business vendor',
  ownerName: item?.owner_name || item?.owner || item?.contact_name || 'Owner not available',
  description: item?.description || item?.about || item?.details || 'No description available.',
  image: normalizeImage(item),
  phoneCode: item?.phone_code ?? '',
  phone: item?.phone ?? '',
  email: item?.email ?? '',
  address: item?.address ?? item?.location ?? '',
  website: item?.website ?? '',
  latitude: item?.latitude ?? '',
  longitude: item?.longitude ?? '',
  doc1: item?.doc1 ?? '',
  doc2: item?.doc2 ?? '',
  doc3: item?.doc3 ?? '',
  badge: item?.status || item?.type || 'Business vendor'
})

const getListCollections = (data) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) {
    return {
      vendorItems: payload
    }
  }
  return {
    vendorItems:
      payload?.vendors ||
      payload?.business_vendors ||
      payload?.businessVendor ||
      payload?.items ||
      payload?.list ||
      []
  }
}

const fetchBusinessVendors = async () => {
  vendorId.value = getAuthUserId()
  serviceListError.value = ''

  if (!vendorId.value) {
    serviceListError.value = 'Vendor id (`s_id`) is not available in auth storage.'
    serviceVendors.value = []
    return
  }

  serviceListLoading.value = true
  try {
    const formData = new FormData()
    formData.append('s_id', vendorId.value)
    const { data } = await api.listBusinessVendor(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Failed to load business vendors')
    }

    const collections = getListCollections(data)
    const normalizedVendors = Array.isArray(collections.vendorItems)
      ? collections.vendorItems.map(normalizeBusinessVendor)
      : []

    serviceVendors.value = normalizedVendors
  } catch (error) {
    serviceListError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to load business vendors'
    serviceVendors.value = []
  } finally {
    serviceListLoading.value = false
  }
}

const navigateToBusinessEditor = (business = null, tab = 'business') => {
  if (business?.id) {
    router.push({
      name: 'business-edit',
      params: { id: business.id },
      query: { tab }
    })
    return
  }

  router.push({
    name: 'business-create',
    query: { tab: 'business' }
  })
}

const navigateToBusinessDetail = (business = null) => {
  if (!business?.id) return
  router.push({
    name: 'business-detail',
    params: { id: business.id }
  })
}

const toggleBusinessMenu = (businessId) => {
  openBusinessMenuId.value = openBusinessMenuId.value === businessId ? null : businessId
}

const handleVendorMenuAction = (action, vendor) => {
  openBusinessMenuId.value = null
  if (action === 'view') {
    navigateToBusinessDetail(vendor)
    return
  }
  if (action === 'edit') {
    navigateToBusinessEditor(vendor, 'business')
    return
  }
  if (action === 'delete') {
    deleteServiceVendor(vendor.id)
  }
}

const deleteServiceVendor = async (id) => {
  const result = await Swal.fire({
    title: 'Delete business?',
    text: 'This business will be removed.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626'
  })

  if (!result.isConfirmed) return

  try {
    const formData = new FormData()
    formData.append('business_id', id)
    const { data } = await api.deleteBusinessVendor(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Failed to delete business')
    }

    serviceVendors.value = serviceVendors.value.filter((item) => item.id !== id)
    await fetchBusinessVendors()
    await Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: data?.msg || 'Business deleted',
      showConfirmButton: false,
      timer: 1600,
      timerProgressBar: true
    })
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Delete failed',
      text: error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to delete business'
    })
  }
}

onMounted(() => {
  fetchBusinessVendors()
})
</script>
